import type { FC } from 'react';
import type { Maps, Coords } from 'google-map-react';
import GoogleMap from 'google-map-react';
import type { LocationMapMarkerProps } from './LocationMapMarker';
import LocationMapMarker from './LocationMapMarker';
import {
    type ArchiProMapVariables,
    hasMapCoordinates,
    GOOGLE_MAP_STYLES,
} from './LocationConstant';
import type {
    LocationDataType,
    ContactLoaderData,
} from '@modules/professional/type/contact-page-types';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useIsSafari } from '@modules/root/hook';
import { useRootData } from '@modules/root';

interface LocationMapProps {
    zoom: number;
    center: Coords;
    filteredLocations: LocationDataType[];
    /**
     * Centre, zoom and viewport restriction, from `useContactLocationMap`. Taken as a prop
     * rather than derived here so the restriction always agrees with the `center`/`zoom`
     * the hook computed — the two callers pass different location lists, so deriving it
     * twice let one country's bounding box clamp a viewport positioned for another.
     */
    mapVariables: ArchiProMapVariables;
    hoveredLocation?: LocationDataType;
    clickedLocation?: LocationDataType;
    onMapMarkerHover: (location: LocationDataType) => void;
    onMapMarkerClick: (location: LocationDataType, key?: number) => void;
    onMapPopupClose?: () => void;
    linkedProfiles: ContactLoaderData['linkedProfiles'];
    /** Whether a clicked pin opens the detail popup on mobile. Defaults to true. */
    showMobilePopup?: boolean;
}
export const LocationMap: FC<LocationMapProps> = ({
    zoom,
    center,
    filteredLocations,
    mapVariables,
    hoveredLocation,
    clickedLocation,
    onMapMarkerHover,
    onMapMarkerClick,
    onMapPopupClose,
    linkedProfiles,
    showMobilePopup = true,
}) => {
    const { config } = useRootData();
    const { mobile: isMobile } = useAppDisplayModeContext();
    const isSafari = useIsSafari();

    const onChildClick = (key: number, childProps: LocationMapMarkerProps) => {
        const location = filteredLocations.find(
            (m) => m.ID === childProps.locationID,
        );
        if (location) {
            onMapMarkerClick(location, key);
        }
    };

    const onChildMouseEnter = (
        key: number,
        childProps: LocationMapMarkerProps,
    ) => {
        const location = filteredLocations.find(
            (m) => m.ID === childProps.locationID,
        );
        if (hoveredLocation?.ID !== childProps.locationID && location) {
            if (isMobile && isSafari) {
                onMapMarkerClick(location, key);
            } else {
                onMapMarkerHover(location);
            }
        }
    };

    const mapOptions = (maps: Maps) => {
        return {
            styles: GOOGLE_MAP_STYLES,
            fullscreenControl: false,
            zoomControl: true,
            zoomControlOptions: {
                position: maps.ControlPosition.TOP_RIGHT,
            },
            minZoom: mapVariables.zoom.min,
            maxZoom: mapVariables.zoom.max,
            restriction: mapVariables.restriction,
            clickableIcons: false,
        };
    };
    return (
        <GoogleMap
            bootstrapURLKeys={{
                key: config.pixels.websiteGoogleMapKey,
                language: 'en',
                region: 'new zealand',
            }}
            center={center}
            zoom={zoom}
            options={mapOptions}
            onChildClick={onChildClick}
            onChildMouseEnter={onChildMouseEnter}
        >
            {filteredLocations.map(
                (locationData, index) =>
                    locationData?.ID &&
                    hasMapCoordinates(locationData) && (
                        <LocationMapMarker
                            key={index}
                            lat={locationData.MapCoordinates.Latitude}
                            lng={locationData.MapCoordinates.Longitude}
                            locationID={locationData.ID}
                            hoveredLocation={hoveredLocation}
                            clickedLocation={clickedLocation}
                            onMapPopupClose={onMapPopupClose}
                            linkedProfiles={linkedProfiles}
                            showMobilePopup={showMobilePopup}
                        />
                    ),
            )}
        </GoogleMap>
    );
};
